/**
 * RequireAuth — Route guard that:
 * 1. Redirects unauthenticated users to login (with a brief delay to show helper note)
 * 2. Blocks authenticated users whose email is not on the approved team list
 */

import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { APPROVED_TEAM_EMAILS } from "@shared/const";
import { Loader2, ShieldX } from "lucide-react";
import { useEffect } from "react";

interface RequireAuthProps {
  children: React.ReactNode;
}

export default function RequireAuth({ children }: RequireAuthProps) {
  const { user, loading, logout } = useAuth();

  // Redirect to login after a short delay (gives parents time to see the helper note)
  useEffect(() => {
    if (!loading && !user) {
      const timer = setTimeout(() => {
        window.location.href = getLoginUrl();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#fffdf5" }}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2
            className="w-8 h-8 animate-spin"
            style={{ color: "#3057bf" }}
          />
          <p
            className="text-sm"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#112451",
              opacity: 0.6,
            }}
          >
            Checking access...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#fffdf5" }}
      >
        <div className="flex flex-col items-center gap-4 max-w-sm mx-4 text-center">
          <Loader2
            className="w-8 h-8 animate-spin"
            style={{ color: "#3057bf" }}
          />
          <p
            className="text-sm"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#112451",
              opacity: 0.6,
            }}
          >
            Redirecting to staff login...
          </p>
          <div
            className="mt-4 p-4 rounded-xl"
            style={{
              backgroundColor: "#f0f4ff",
              border: "1px solid rgba(48, 87, 191, 0.15)",
            }}
          >
            <p
              className="text-sm font-medium mb-1"
              style={{
                fontFamily: "'Source Sans 3', sans-serif",
                color: "#112451",
              }}
            >
              Not a staff member?
            </p>
            <p
              className="text-xs"
              style={{
                fontFamily: "'Source Sans 3', sans-serif",
                color: "#112451",
                opacity: 0.7,
                lineHeight: 1.5,
              }}
            >
              Looking for your child's planner? Check the email from Assist Therapy for your direct link and access code.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if the user's email is on the approved team list
  const userEmail = user.email?.toLowerCase() ?? "";
  const isApproved = APPROVED_TEAM_EMAILS.some(
    (email) => email.toLowerCase() === userEmail
  );

  if (!isApproved) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#fffdf5" }}
      >
        <div
          className="max-w-md w-full mx-4 p-8 rounded-2xl text-center"
          style={{
            backgroundColor: "#ffffff",
            boxShadow: "0 4px 24px rgba(17, 36, 81, 0.08)",
          }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "#fef3f2" }}
          >
            <ShieldX className="w-8 h-8" style={{ color: "#dc2626" }} />
          </div>
          <h1
            className="text-2xl font-bold mb-3"
            style={{
              fontFamily: "'DM Serif Display', serif",
              color: "#112451",
            }}
          >
            Access Restricted
          </h1>
          <p
            className="mb-2"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#112451",
              opacity: 0.7,
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            This area is restricted to the Assist Developmental Therapy team.
          </p>
          <p
            className="mb-6"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#112451",
              opacity: 0.5,
              fontSize: "0.85rem",
            }}
          >
            Signed in as: {user.email || user.name || "Unknown"}
          </p>
          <button
            onClick={() => logout()}
            className="px-6 py-2.5 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              backgroundColor: "#112451",
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
